// app/src/Schedule.jsx — shifts, swaps, and time-off.
//
// Everyone sees the schedule. Managers add shifts and approve time-off; crew can
// offer their own shift for swap, pick up an offered shift, and request time off.

function fmtDay(ms) { return new Date(ms).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }); }
function fmtTime(ms) { return ms ? new Date(ms).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : ''; }
// Monday 00:00 of the week containing `d`.
function startOfWeek(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); const dow = (x.getDay() + 6) % 7; x.setDate(x.getDate() - dow); return x; }
function sameWeek(ms, weekStart) { return ms >= weekStart.getTime() && ms < weekStart.getTime() + 7 * 864e5; }
// Shift length in hours (one decimal), e.g. 8 or 7.5; null if unknown.
function hoursOf(s) { return (s.start && s.end) ? Math.round((s.end - s.start) / 360000) / 10 : null; }
// Left-accent colour for a shift card by status / ownership.
function accentOf(s, mine) { if (mine) return 'var(--primary)'; if (s.status === 'assigned') return 'var(--ok)'; if (s.status === 'swap_requested' || s.status === 'open') return 'var(--accent)'; return 'var(--line)'; }

function ScheduleScreen() {
  const wid = window.crew && window.crew.state.activeWorkplaceId;
  const me = (window.crew && window.crew.state.me) || { workplaces: [], profile: {} };
  const myWp = (me.workplaces || []).find((w) => w.workplaceId === wid) || {};
  const myId = me.profile && me.profile.userId;
  const myName = myWp.displayName || t('common.member', 'Member');
  const isManager = myWp.role === 'owner' || myWp.role === 'manager';

  const [tab, setTab] = React.useState('shifts');
  const [shifts, setShifts] = React.useState(null);
  const [timeoff, setTimeoff] = React.useState(null);
  const [modal, setModal] = React.useState(null); // 'shift' | 'timeoff'
  const [review, setReview] = React.useState(null); // shift being reviewed for swap

  const load = React.useCallback(async () => {
    if (!wid) return;
    try {
      const [s, to] = await Promise.all([
        window.crew.api(`/workplaces/${wid}/shifts`, { workplaceId: wid }),
        window.crew.api(`/workplaces/${wid}/timeoff`, { workplaceId: wid }),
      ]);
      setShifts((s && s.shifts) || []);
      setTimeoff((to && to.requests) || []);
    } catch (e) { console.warn('[schedule] load', e && e.message); setShifts([]); setTimeoff([]); }
  }, [wid]);

  React.useEffect(() => { load(); }, [load]);

  const act = async (path, body) => { try { await window.crew.api(path, { method: 'POST', workplaceId: wid, body: body || {} }); await load(); } catch (e) { console.warn(e); } };
  const offerSwap = (s) => act(`/workplaces/${wid}/swaps`, { shiftId: s.shiftId, requesterName: myName });
  const pickUp = async (s) => {
    // find the pending swap for this shift, then accept it (server is atomic, so a
    // concurrent pickup just gets a 409 and we refresh to the latest state).
    try {
      const r = await window.crew.api(`/workplaces/${wid}/swaps`, { workplaceId: wid });
      const sw = ((r && r.swaps) || []).find((x) => x.shiftId === s.shiftId && x.status === 'pending');
      if (sw) await act(`/workplaces/${wid}/swaps/${sw.swapId}/accept`, { acceptorName: myName });
      else await load(); // already taken — refresh
    } catch (e) { console.warn(e); await load(); }
  };

  // Derived week + swap data (used by the strip, banner, and review sheet).
  const list = shifts || [];
  const weekStart = startOfWeek(new Date());
  const weekDays = Array.from({ length: 7 }, (_, i) => new Date(weekStart.getTime() + i * 864e5));
  const todayKey = new Date().toDateString();
  const myShiftDays = new Set(list.filter((s) => s.assigneeId === myId).map((s) => new Date(s.start).toDateString()));
  const swapShift = list.find((s) => s.status === 'swap_requested' && s.assigneeId !== myId);

  return (
    <KShell tab="schedule">
      <KHeader title={t('nav.schedule', 'Schedule')} />
      <div style={{ display: 'flex', gap: 8, padding: '10px 14px' }}>
        {[['shifts', t('schedule.shifts', 'Shifts')], ['timeoff', t('schedule.timeoff', 'Time off')]].map(([k, lbl]) => (
          <button key={k} onClick={() => setTab(k)} style={{ flex: 1, padding: 8, borderRadius: 999, border: '1px solid var(--line)', cursor: 'pointer', background: tab === k ? 'var(--primary)' : 'var(--surface)', color: tab === k ? 'var(--primary-ink)' : 'var(--ink)', fontWeight: 700, fontSize: 13 }}>{lbl}</button>
        ))}
      </div>

      {tab === 'shifts' && (
        <div style={{ padding: '4px 14px 10px' }}>
          {/* Week strip — today highlighted; a dot marks days you're on. */}
          <div style={{ display: 'flex', gap: 4, padding: '0 0 12px' }}>
            {weekDays.map((d, i) => {
              const key = d.toDateString();
              const isToday = key === todayKey;
              return (
                <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: 9.5, color: isToday ? 'var(--primary)' : 'var(--on-dim)', fontWeight: 700, marginBottom: 5 }}>{['M', 'T', 'W', 'T', 'F', 'S', 'S'][i]}</div>
                  <div style={{ fontSize: 12.5, fontWeight: 800, padding: '5px 0', borderRadius: 10, color: isToday ? 'var(--primary-ink)' : 'var(--on)', background: isToday ? 'var(--primary)' : 'transparent' }}>{d.getDate()}</div>
                  <div style={{ height: 5, marginTop: 3, display: 'flex', justifyContent: 'center' }}>{myShiftDays.has(key) && <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--accent)' }} />}</div>
                </div>
              );
            })}
          </div>

          {/* Swap request banner */}
          {swapShift && (
            <div onClick={() => setReview(swapShift)} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--accent-soft)', borderRadius: 'var(--radius-sm)', padding: '11px 12px', marginBottom: 12, borderLeft: '3px solid var(--accent)', cursor: 'pointer' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}><polyline points="17 1 21 5 17 9" /><path d="M3 11V9a4 4 0 0 1 4-4h14" /><polyline points="7 23 3 19 7 15" /><path d="M21 13v2a4 4 0 0 1-4 4H3" /></svg>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>{t('schedule.swapBanner', '{name} wants to cover their shift', { name: swapShift.assigneeName || t('common.member', 'Member') })}</div>
                <div style={{ fontSize: 11.5, color: 'var(--sub)' }}>{fmtDay(swapShift.start)} · {t('schedule.tapReview', 'Tap to review')}</div>
              </div>
              <span style={{ fontSize: 16, color: 'var(--accent)', fontWeight: 700, flex: 'none' }}>›</span>
            </div>
          )}

          {isManager && <button onClick={() => setModal('shift')} style={dashBtn}>+ {t('schedule.addShift', 'Add shift')}</button>}
          {shifts == null && <div style={{ color: 'var(--on-dim)', padding: 12 }}>…</div>}
          {shifts != null && shifts.length === 0 && <div style={{ color: 'var(--on-soft)', textAlign: 'center', padding: 20 }}>{t('schedule.noShifts', 'No shifts scheduled.')}</div>}
          {(shifts || []).map((s) => {
            const mine = s.assigneeId === myId;
            const h = hoursOf(s);
            return (
              <div key={s.shiftId} style={{ display: 'flex', overflow: 'hidden', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--radius)', marginBottom: 10 }}>
                <div style={{ width: 5, background: accentOf(s, mine), flex: 'none' }} />
                <div style={{ flex: 1, minWidth: 0, padding: '12px 14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <div style={{ fontWeight: 700, color: 'var(--ink)', fontSize: 14 }}>{fmtDay(s.start)}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 'none' }}>
                      {h != null && <span style={{ fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 999, background: 'var(--primary-soft)', color: 'var(--primary)' }}>{h}h</span>}
                      <span style={statusPill(s.status)}>{statusLabel(s.status || 'open')}</span>
                    </div>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--sub)', margin: '4px 0' }}>
                    {fmtTime(s.start)}{s.end ? `–${fmtTime(s.end)}` : ''}{s.role ? ` · ${s.role}` : ''}{s.station ? ` · ${s.station}` : ''}
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--ink)' }}>{s.assigneeName ? `👤 ${s.assigneeName}` : t('schedule.unassigned', 'Unassigned')}</div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    {mine && s.status === 'assigned' && <button onClick={() => offerSwap(s)} style={miniBtn}>{t('schedule.offerSwap', 'Offer swap')}</button>}
                    {!mine && s.status === 'swap_requested' && <button onClick={() => setReview(s)} style={miniBtnPrimary}>{t('schedule.review', 'Review')}</button>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === 'timeoff' && (
        <div style={{ padding: '4px 14px 10px' }}>
          <button onClick={() => setModal('timeoff')} style={dashBtn}>+ {t('schedule.requestTimeoff', 'Request time off')}</button>
          {timeoff == null && <div style={{ color: 'var(--on-dim)', padding: 12 }}>…</div>}
          {timeoff != null && timeoff.length === 0 && <div style={{ color: 'var(--on-soft)', textAlign: 'center', padding: 20 }}>{t('schedule.noRequests', 'No requests.')}</div>}
          {(timeoff || []).map((r) => (
            <div key={r.requestId} style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ fontWeight: 700, color: 'var(--ink)', fontSize: 14 }}>{r.userName || t('common.member', 'Member')}</div>
                <span style={statusPill(r.status)}>{statusLabel(r.status || 'pending')}</span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--sub)', margin: '4px 0' }}>{r.startDate} → {r.endDate}</div>
              {r.reason && <div style={{ fontSize: 12.5, color: 'var(--ink)' }}>{r.reason}</div>}
              {isManager && r.status === 'pending' && (
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button onClick={() => act(`/workplaces/${wid}/timeoff/${r.requestId}/approve`)} style={miniBtnPrimary}>{t('schedule.approve', 'Approve')}</button>
                  <button onClick={() => act(`/workplaces/${wid}/timeoff/${r.requestId}/deny`)} style={miniBtn}>{t('schedule.deny', 'Deny')}</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {modal === 'shift' && <AddShift wid={wid} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
      {modal === 'timeoff' && <RequestTimeoff wid={wid} myName={myName} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}

      {review && (() => {
        // Hours must be counted in the REVIEWED shift's week, not always the current
        // one (the offer may be for a later week). Guard against shifts with no end
        // time so a missing end never reads as "no clash".
        const SHIFT_FALLBACK_MS = 8 * 3600e3;
        const rEnd = review.end || (review.start + SHIFT_FALLBACK_MS);
        const clash = list.some((s) => s.assigneeId === myId && s.shiftId !== review.shiftId
          && s.start < rEnd && review.start < (s.end || (s.start + SHIFT_FALLBACK_MS)));
        const revWeek = startOfWeek(new Date(review.start));
        const inThisWeek = revWeek.getTime() === weekStart.getTime();
        const wkHours = list.filter((s) => s.assigneeId === myId && sameWeek(s.start, revWeek)).reduce((a, s) => a + (hoursOf(s) || 0), 0);
        const after = Math.round((wkHours + (hoursOf(review) || 0)) * 10) / 10;
        return (
          <Sheet onClose={() => setReview(null)} title={t('schedule.swapTitle', 'Shift swap request')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 14 }}>
              <Avatar name={review.assigneeName} size={40} />
              <div>
                <div style={{ fontWeight: 700, color: 'var(--ink)', fontSize: 14 }}>{review.assigneeName || t('common.member', 'Member')}</div>
                <div style={{ fontSize: 11.5, color: 'var(--sub)' }}>{t('schedule.offeringShift', 'Offering their shift')}</div>
              </div>
            </div>
            <div style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)', padding: 14, marginBottom: 12 }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.4px', color: 'var(--sub)', marginBottom: 4 }}>{t('schedule.theShift', 'THE SHIFT')}</div>
              <div style={{ fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: 18, color: 'var(--ink)' }}>{fmtDay(review.start)}</div>
              <div style={{ fontSize: 13, color: 'var(--sub)', marginTop: 2 }}>{fmtTime(review.start)}{review.end ? `–${fmtTime(review.end)}` : ''}{review.role ? ` · ${review.role}` : ''}{hoursOf(review) != null ? ` · ${hoursOf(review)}h` : ''}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: clash ? 'var(--accent-soft)' : 'var(--ok-soft)', borderRadius: 'var(--radius-sm)', padding: '11px 12px', marginBottom: 14 }}>
              <span style={{ color: clash ? 'var(--accent)' : 'var(--ok)', fontWeight: 800, flex: 'none' }}>{clash ? '!' : '✓'}</span>
              <span style={{ fontSize: 12.5, color: 'var(--ink)' }}>{clash
                ? t('schedule.clash', 'Heads up — this overlaps a shift you already have.')
                : inThisWeek
                  ? t('schedule.noClash', 'No clash with your schedule. This puts you at {h}h this week.', { h: after })
                  : t('schedule.noClashWeek', 'No clash with your schedule. This puts you at {h}h that week.', { h: after })}</span>
            </div>
            <SheetButton onClick={() => { pickUp(review); setReview(null); }}>{t('schedule.acceptCover', 'Accept & cover shift')}</SheetButton>
          </Sheet>
        );
      })()}
    </KShell>
  );
}

function AddShift({ wid, onClose, onDone }) {
  const [date, setDate] = React.useState('');
  const [start, setStart] = React.useState('09:00');
  const [end, setEnd] = React.useState('17:00');
  const [role, setRole] = React.useState('');
  const [assignee, setAssignee] = React.useState('');
  const [members, setMembers] = React.useState([]);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => { window.crew.api(`/workplaces/${wid}/members`, { workplaceId: wid }).then((r) => setMembers((r && r.members) || [])).catch(() => {}); }, [wid]);

  const create = async () => {
    if (!date || busy) return;
    setBusy(true);
    try {
      const startMs = new Date(`${date}T${start}`).getTime();
      const endMs = new Date(`${date}T${end}`).getTime();
      const m = members.find((x) => x.userId === assignee);
      await window.crew.api(`/workplaces/${wid}/shifts`, { method: 'POST', workplaceId: wid, body: { start: startMs, end: endMs, role, assigneeId: assignee || null, assigneeName: m ? m.displayName : null } });
      onDone();
    } catch (e) { console.warn(e); setBusy(false); }
  };

  return (
    <Sheet onClose={onClose} title={t('schedule.addShift', 'Add shift')}>
      <Field label={t('schedule.date', 'Date')}><input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inp} /></Field>
      <div style={{ display: 'flex', gap: 8 }}>
        <Field label={t('schedule.startTime', 'Start')}><input type="time" value={start} onChange={(e) => setStart(e.target.value)} style={inp} /></Field>
        <Field label={t('schedule.endTime', 'End')}><input type="time" value={end} onChange={(e) => setEnd(e.target.value)} style={inp} /></Field>
      </div>
      <Field label={t('schedule.roleStation', 'Role / station')}><input value={role} onChange={(e) => setRole(e.target.value)} placeholder={t('schedule.rolePlaceholder', 'e.g. Server · Dinner')} style={inp} /></Field>
      <Field label={t('schedule.assignTo', 'Assign to')}>
        <select value={assignee} onChange={(e) => setAssignee(e.target.value)} style={inp}>
          <option value="">{t('schedule.unassigned', 'Unassigned')}</option>
          {members.map((m) => <option key={m.userId} value={m.userId}>{m.displayName || t('common.member', 'Member')}</option>)}
        </select>
      </Field>
      <SheetButton onClick={create} disabled={!date || busy}>{busy ? '…' : t('schedule.addShift', 'Add shift')}</SheetButton>
    </Sheet>
  );
}

function RequestTimeoff({ wid, myName, onClose, onDone }) {
  const [startDate, setStartDate] = React.useState('');
  const [endDate, setEndDate] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const submit = async () => {
    if (!startDate || !endDate || busy) return;
    setBusy(true);
    try { await window.crew.api(`/workplaces/${wid}/timeoff`, { method: 'POST', workplaceId: wid, body: { startDate, endDate, reason, userName: myName } }); onDone(); }
    catch (e) { console.warn(e); setBusy(false); }
  };
  return (
    <Sheet onClose={onClose} title={t('schedule.requestTimeoff', 'Request time off')}>
      <Field label={t('schedule.from', 'From')}><input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={inp} /></Field>
      <Field label={t('schedule.to', 'To')}><input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={inp} /></Field>
      <Field label={t('schedule.reason', 'Reason (optional)')}><input value={reason} onChange={(e) => setReason(e.target.value)} style={inp} /></Field>
      <SheetButton onClick={submit} disabled={!startDate || !endDate || busy}>{busy ? '…' : t('schedule.submitRequest', 'Submit request')}</SheetButton>
    </Sheet>
  );
}

// ── small shared bits ──
function Sheet({ title, children, onClose }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(20,12,8,.45)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 50 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 430, background: 'var(--surface)', borderTopLeftRadius: 22, borderTopRightRadius: 22, padding: 20, maxHeight: '85%', overflowY: 'auto' }}>
        <div style={{ fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: 18, color: 'var(--ink)', marginBottom: 14 }}>{title}</div>
        {children}
        <button onClick={onClose} style={{ width: '100%', marginTop: 8, padding: 10, border: 'none', background: 'none', color: 'var(--sub)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{t('common.cancel', 'Cancel')}</button>
      </div>
    </div>
  );
}
function Field({ label, children }) {
  return <div style={{ flex: 1, marginBottom: 12 }}><div style={{ fontSize: 12, fontWeight: 700, color: 'var(--sub)', marginBottom: 6 }}>{label}</div>{children}</div>;
}
function SheetButton({ onClick, disabled, children }) {
  return <button onClick={onClick} disabled={disabled} style={{ width: '100%', padding: 13, border: 'none', borderRadius: 'var(--radius-sm)', background: 'var(--primary)', color: 'var(--primary-ink)', fontWeight: 700, fontSize: 15, cursor: 'pointer', opacity: disabled ? 0.6 : 1 }}>{children}</button>;
}
// Localized, upper-cased status badge text (no-op upper-case for non-Latin scripts).
function statusLabel(s) { return t('status.' + s, s).toUpperCase(); }

const card = { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--radius)', padding: 14, marginBottom: 10 };
const dashBtn = { width: '100%', padding: 11, marginBottom: 12, border: '1px dashed var(--line)', background: 'var(--surface)', borderRadius: 'var(--radius-sm)', color: 'var(--primary)', fontWeight: 700, fontSize: 14, cursor: 'pointer' };
const miniBtn = { border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 999, padding: '6px 14px', fontSize: 12.5, fontWeight: 700, color: 'var(--ink)', cursor: 'pointer' };
const miniBtnPrimary = { ...miniBtn, background: 'var(--primary)', color: 'var(--primary-ink)', border: 'none' };
const inp = { width: '100%', padding: '11px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 15, boxSizing: 'border-box' };
function statusPill(s) {
  const map = { open: ['var(--accent-soft)', 'var(--accent)'], assigned: ['var(--ok-soft)', 'var(--ok)'], swap_requested: ['var(--accent-soft)', 'var(--accent)'], pending: ['var(--accent-soft)', 'var(--accent)'], approved: ['var(--ok-soft)', 'var(--ok)'], accepted: ['var(--ok-soft)', 'var(--ok)'], denied: ['var(--surface-2)', 'var(--sub)'], declined: ['var(--surface-2)', 'var(--sub)'] };
  const [bg, fg] = map[s] || ['var(--surface-2)', 'var(--sub)'];
  return { fontSize: 9, fontWeight: 800, letterSpacing: '.4px', padding: '3px 8px', borderRadius: 999, background: bg, color: fg };
}

Object.assign(window, { ScheduleScreen });
